import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, ArrowDownToLine, TrendingUp, ArrowUpFromLine, Gift, ArrowRight, Users, Layers } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader, fmt, fmtDateTime, StatusBadge, EmptyState } from "@/components/shared";
import { SharesSummaryCard } from "@/components/SharesSummaryCard";

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div data-testid={testId} className="glass glass-hover rounded-2xl p-5 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4.5 h-4.5" /></div>
      </div>
      <div className="font-mono text-2xl font-bold text-white">{fmt(value)} <span className="text-sm text-slate-500 font-sans">USDT</span></div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((p) => <p key={p.name} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>)}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === null) return <Loader label="Loading dashboard..." />;
  const b = data?.balance || {};
  const inv = data?.investments || {};

  return (
    <div className="space-y-6" data-testid="user-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">Dashboard</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Welcome, {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1 font-mono">ID: {user?.id?.slice(0, 8)} · Joined {fmtDateTime(user?.created_at)}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => nav("/deposit")} data-testid="dash-deposit-btn" className="btn-glow px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950 inline-flex items-center gap-2">
            <ArrowDownToLine className="w-4 h-4" /> Deposit
          </button>
          <button onClick={() => nav("/withdrawal")} data-testid="dash-withdraw-btn" className="glass glass-hover px-5 py-2.5 rounded-full text-sm font-semibold text-slate-100 inline-flex items-center gap-2">
            <ArrowUpFromLine className="w-4 h-4" /> Withdraw
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard testId="dashboard-balance-card" icon={Wallet} label="Available Balance" value={b.available_balance} accent="bg-cyan-500/15 text-cyan-300" />
        <StatCard testId="card-total-deposit" icon={ArrowDownToLine} label="Total Deposit" value={b.total_deposit} accent="bg-blue-500/15 text-blue-300" />
        <StatCard testId="card-total-earnings" icon={TrendingUp} label="Total Earnings" value={b.total_earnings} accent="bg-emerald-500/15 text-emerald-300" />
        <StatCard testId="card-total-withdrawal" icon={ArrowUpFromLine} label="Total Withdrawal" value={b.total_withdrawal} accent="bg-amber-500/15 text-amber-300" />
        <StatCard testId="card-referral-rewards" icon={Gift} label="Referral Rewards" value={b.total_referral_reward} accent="bg-fuchsia-500/15 text-fuchsia-300" />
      </div>

      <div className="glass rounded-2xl p-5 lg:p-6 flex flex-col md:flex-row md:items-center gap-5" data-testid="dashboard-investments-card">
        <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center shrink-0"><Layers className="w-5 h-5 text-cyan-300" /></div>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><p className="text-xs text-slate-500 uppercase tracking-wider">Active Plans</p><p className="font-mono text-xl text-white" data-testid="inv-active-count">{inv.active_count || 0}</p></div>
          <div><p className="text-xs text-slate-500 uppercase tracking-wider">Locked Principal</p><p className="font-mono text-xl text-white" data-testid="inv-total-invested">{fmt(inv.total_invested)} <span className="text-xs text-slate-500 font-sans">USDT</span></p></div>
          <div><p className="text-xs text-slate-500 uppercase tracking-wider">Daily ROI (Mon–Fri)</p><p className="font-mono text-xl text-emerald-300" data-testid="inv-daily">{fmt(inv.daily_estimate)} <span className="text-xs text-slate-500 font-sans">USDT</span></p></div>
          <div><p className="text-xs text-slate-500 uppercase tracking-wider">Next Credit</p><p className="font-mono text-sm text-slate-200 mt-1.5" data-testid="inv-next-credit">{inv.next_credit_at ? fmtDateTime(inv.next_credit_at) : "—"}</p></div>
        </div>
        <button onClick={() => nav("/plans-portal")} data-testid="dash-plans-btn" className="glass glass-hover px-5 py-2.5 rounded-full text-sm text-cyan-300 inline-flex items-center gap-2 shrink-0">
          Manage Plans <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <SharesSummaryCard />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-lg text-white">Activity (7 days)</h3>
            <span className="text-xs text-slate-500 flex items-center gap-3">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyan-400" /> Deposit</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Earnings</span>
            </span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.chart}>
              <defs>
                <linearGradient id="gDep" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} /><stop offset="100%" stopColor="#00F0FF" stopOpacity={0} /></linearGradient>
                <linearGradient id="gEarn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="deposit" name="Deposit" stroke="#00F0FF" fill="url(#gDep)" strokeWidth={2} />
              <Area type="monotone" dataKey="earnings" name="Earnings" stroke="#10B981" fill="url(#gEarn)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white mb-5">Referrals</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/15 flex items-center justify-center"><Users className="w-5 h-5 text-cyan-300" /></div>
              <div><p className="font-mono text-2xl font-bold text-white">{data.referrals.total}</p><p className="text-xs text-slate-500">Total Referrals</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center"><Gift className="w-5 h-5 text-emerald-300" /></div>
              <div><p className="font-mono text-2xl font-bold text-white">{data.referrals.active}</p><p className="text-xs text-slate-500">Active Referrals</p></div>
            </div>
            <button onClick={() => nav("/referrals")} className="w-full mt-2 glass-hover glass rounded-lg py-2.5 text-sm text-cyan-300 inline-flex items-center justify-center gap-2">
              View Referral Program <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-lg text-white">Recent Transactions</h3>
          <button onClick={() => nav("/transactions")} className="text-sm text-cyan-400 hover:underline">View all</button>
        </div>
        {data.recent_transactions?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-white/5">
                <th className="pb-3 font-medium">Type</th><th className="pb-3 font-medium">Amount</th><th className="pb-3 font-medium">Status</th><th className="pb-3 font-medium">Date</th>
              </tr></thead>
              <tbody>
                {data.recent_transactions.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 last:border-0">
                    <td className="py-3 capitalize text-slate-200">{t.type.replace("_", " ")}</td>
                    <td className="py-3 font-mono text-white">{fmt(t.amount)}</td>
                    <td className="py-3"><StatusBadge status={t.status} /></td>
                    <td className="py-3 text-slate-500">{fmtDateTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState title="No transactions yet" subtitle="Your deposits, withdrawals and rewards will appear here." />}
      </div>
    </div>
  );
}
